import React, { createContext, useContext, useMemo } from 'react';
import type { PhaseEvent } from '../hooks/usePersonaToolProgress';

// Ticking runtime state for the Chat DAG — deliberately split from
// PersonaChatUIContext (which holds stable imperatives) so the DAG's
// high-frequency updates don't cascade re-renders into MessageBubbles
// and PersonaContextPanel rows.

const EMPTY_PHASE_EVENTS: PhaseEvent[] = [];
const EMPTY_COLLAPSED: ReadonlySet<string> = new Set();

const NOOP_SET_FOCUS = (_: string | null) => {
  /* default no-op — real setter comes from PersonaChat */
};

export interface ChatDagRuntimeValue {
  inFlightTurnId: string | null;
  viewportTurnId: string | null;
  // v0.4 focal chain inputs — PersonaChat owns the state; provider
  // is pure prop-passthrough.
  tabFocusTurnId: string | null;
  lastInFlightTurnId: string | null;
  inFlightStartedAtMs: number | null;
  // Derived inside the provider: inFlight ?? viewport ?? tabFocus ?? lastInFlight.
  focalTurnId: string | null;
  // Exposed for useTabFocalTrigger (Task 11).
  setTabFocusTurnId: (id: string | null) => void;
  collapsedFanIds: ReadonlySet<string>;
  livePhaseEvents: PhaseEvent[];
  wsLastEventMs: number | null;
  errorTurnId: string | null;
  // Controls-panel globals. Default sane values so consumers that
  // don't wire them still get the v0.3 canonical rendering.
  allFansCollapsed: boolean;
  showPhaseStacks: boolean;
  // Per-turn phase-stack collapse: each turnKey (user msg id) in the
  // set renders as a single collapsed_phase_stack card instead of
  // full rounds/tools/peers.
  collapsedPhaseStackIds: ReadonlySet<string>;
  allPhaseStacksCollapsed: boolean;
}

const defaultValue: ChatDagRuntimeValue = {
  inFlightTurnId: null,
  viewportTurnId: null,
  tabFocusTurnId: null,
  lastInFlightTurnId: null,
  inFlightStartedAtMs: null,
  focalTurnId: null,
  setTabFocusTurnId: NOOP_SET_FOCUS,
  collapsedFanIds: EMPTY_COLLAPSED,
  livePhaseEvents: EMPTY_PHASE_EVENTS,
  wsLastEventMs: null,
  errorTurnId: null,
  allFansCollapsed: false,
  showPhaseStacks: true,
  collapsedPhaseStackIds: EMPTY_COLLAPSED,
  allPhaseStacksCollapsed: false,
};

const ChatDagRuntimeContext = createContext<ChatDagRuntimeValue>(defaultValue);

export interface ChatDagRuntimeProviderProps {
  children: React.ReactNode;
  inFlightTurnId?: string | null;
  viewportTurnId?: string | null;
  tabFocusTurnId?: string | null;
  lastInFlightTurnId?: string | null;
  inFlightStartedAtMs?: number | null;
  setTabFocusTurnId?: (id: string | null) => void;
  collapsedFanIds?: ReadonlySet<string>;
  livePhaseEvents?: PhaseEvent[];
  wsLastEventMs?: number | null;
  errorTurnId?: string | null;
  allFansCollapsed?: boolean;
  showPhaseStacks?: boolean;
  collapsedPhaseStackIds?: ReadonlySet<string>;
  allPhaseStacksCollapsed?: boolean;
}

export const ChatDagRuntimeProvider: React.FC<ChatDagRuntimeProviderProps> = ({
  children,
  inFlightTurnId = null,
  viewportTurnId = null,
  tabFocusTurnId = null,
  lastInFlightTurnId = null,
  inFlightStartedAtMs = null,
  setTabFocusTurnId = NOOP_SET_FOCUS,
  collapsedFanIds = EMPTY_COLLAPSED,
  livePhaseEvents = EMPTY_PHASE_EVENTS,
  wsLastEventMs = null,
  errorTurnId = null,
  allFansCollapsed = false,
  showPhaseStacks = true,
  collapsedPhaseStackIds = EMPTY_COLLAPSED,
  allPhaseStacksCollapsed = false,
}) => {
  // v0.4 focal chain — DORMANT (2026-04-24). Lift Pass 1 (inFlight
  // only) revealed that the rich↔compact expand/contract animation
  // is bad UX. Shipping v0.4 as infrastructure: all rich variants,
  // PersonaCardBody, dim context, layout resolveDims, turnBoundingBoxes,
  // setViewport anchor, ariaLabels, a11y wrapping, handles on rich
  // variants — all on disk, all inert. A v0.4.1 plan should design
  // the rich-dispatch UX deliberately (click-to-expand? density
  // slider? always-rich with DL4 mitigation?) and flip this chain
  // back on with the agreed trigger.
  const focalTurnId: string | null = null;
  void inFlightTurnId;
  void viewportTurnId;
  void tabFocusTurnId;
  void lastInFlightTurnId;

  const value = useMemo<ChatDagRuntimeValue>(
    () => ({
      inFlightTurnId,
      viewportTurnId,
      tabFocusTurnId,
      lastInFlightTurnId,
      inFlightStartedAtMs,
      focalTurnId,
      setTabFocusTurnId,
      collapsedFanIds,
      livePhaseEvents,
      wsLastEventMs,
      errorTurnId,
      allFansCollapsed,
      showPhaseStacks,
      collapsedPhaseStackIds,
      allPhaseStacksCollapsed,
    }),
    [
      inFlightTurnId,
      viewportTurnId,
      tabFocusTurnId,
      lastInFlightTurnId,
      inFlightStartedAtMs,
      focalTurnId,
      setTabFocusTurnId,
      collapsedFanIds,
      livePhaseEvents,
      wsLastEventMs,
      errorTurnId,
      allFansCollapsed,
      showPhaseStacks,
      collapsedPhaseStackIds,
      allPhaseStacksCollapsed,
    ],
  );
  return (
    <ChatDagRuntimeContext.Provider value={value}>
      {children}
    </ChatDagRuntimeContext.Provider>
  );
};

export function useChatDagRuntime(): ChatDagRuntimeValue {
  return useContext(ChatDagRuntimeContext);
}

// Opacity priority for DAG nodes (spec rev 1.1 § 9):
//
//   running node                     → 1.0 (always visible — running
//                                     nodes are opacity-exempt)
//   turnKey matches inFlightTurnId   → 1.0
//   turnKey matches viewportTurnId   → 1.0
//   viewportTurnId is null (no scroll yet) → 1.0
//   otherwise                        → 0.35 (dim historical turns)
//
// The !running exemption is load-bearing: a running node MUST stay
// opaque even if the user has scrolled away from it, otherwise the
// "still waiting on peer 4" signal becomes invisible.
export function useDagNodeOpacity(
  turnKey: string | undefined,
  isRunning: boolean,
): { opacity: number; transition: string } {
  const { inFlightTurnId, viewportTurnId } = useChatDagRuntime();
  if (isRunning) return { opacity: 1, transition: 'opacity 150ms ease' };
  if (viewportTurnId == null) return { opacity: 1, transition: 'opacity 150ms ease' };
  const key = turnKey ?? null;
  const isInFlight = inFlightTurnId != null && inFlightTurnId === key;
  const isInViewport = viewportTurnId === key;
  const dim = !isInFlight && !isInViewport;
  return {
    opacity: dim ? 0.35 : 1,
    transition: 'opacity 150ms ease',
  };
}

// Exposed for consumers that want the same EMPTY reference when
// disabled (prevents identity-change re-renders).
export { EMPTY_PHASE_EVENTS, EMPTY_COLLAPSED };
